import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import clsx from "clsx";

import AccountService from "@app/services/http/account.service";
import useObservable from "@core/hooks/use-observable.hook";
import useBusy from "@core/hooks/use-busy.hook";
import { Account } from "@app/types";
import { openPortalDialog } from "@app/services/modal.service";
import { formatDate } from "@app/utils/util";
import { MAX_LEN, PathnameUrl } from "@app/constants";
import { addToast } from "@app/components/toast/toast.service";
import { storeMyInfo } from "@app/store/my-info/my-info.action";
import { useT } from "@app/contexts/preferences.context";
import ChangePasswordModal from "@app/components/change-password-modal";
import { openSupport } from "@app/components/support-chat";
import useLogout from "@app/utils/use-logout.hook";
import { CopyButton, LoadingBlock, Spinner, btn, btnGhost, btnPrimary, btnSm, input } from "@app/components/ui";
import { IcAlert, IcCheck, IcIdCard, IcInfo, IcKeyRound, IcLogOut, IcUser } from "@app/components/icons";

const PHONE_RE = /^(0|\+84)(3|5|7|8|9)\d{8}$/;
const cleanPhone = (v: string) => v.replace(/[\s.-]/g, "");

const panel = "min-w-0 overflow-hidden rounded-[14px] border border-line bg-surface";
const panelHead =
  "flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 max-[520px]:px-4";
const panelTitle = "flex items-center gap-2 text-[17px] font-bold [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:text-brand";
const row = "flex min-w-0 items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0";
const okPill =
  "inline-flex items-center gap-[5px] whitespace-nowrap rounded-full bg-ok-soft px-2.5 py-[3px] text-xs font-semibold text-ok";
const warnPill =
  "inline-flex items-center gap-[5px] whitespace-nowrap rounded-full bg-warn-soft px-2.5 py-[3px] text-xs font-semibold text-warn";

function Notice({ title, text, to, action }: { title: string; text: string; to: string; action: string }) {
  const t = useT();

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[12px] bg-warn-soft px-4 py-3 text-warn">
      <IcAlert className="h-5 w-5 flex-none" />
      <div className="min-w-[200px] flex-1">
        <b className="block text-[13.5px] text-fg">{t(title)}</b>
        <span className="text-[13px] text-muted">{t(text)}</span>
      </div>
      <Link to={to} className={clsx(btn, btnPrimary, btnSm)}>
        {t(action)}
      </Link>
    </div>
  );
}

function ProfilePage() {
  const t = useT();
  const dispatch = useDispatch();
  const logout = useLogout();
  const { subscribeUntilDestroy } = useObservable();
  const { busy, run } = useBusy();

  const [user, setUser] = useState<Account | null>(null);
  const [phone, setPhone] = useState("");
  const [phoneErr, setPhoneErr] = useState("");

  // Lấy thông tin tài khoản hiện tại (như trang cũ)
  useEffect(() => {
    subscribeUntilDestroy(AccountService.getMyInfo(), (res) => {
      if (res) {
        setUser(res);
        setPhone(res.phone || "");
      }
    });
  }, [subscribeUntilDestroy]);

  if (!user) {
    return (
      <div className="px-6 pt-6 max-[900px]:px-4">
        <LoadingBlock />
      </div>
    );
  }

  // Các trạng thái giữ nguyên cách tính của trang cũ
  const isEkycCompleted = user.kyc === 1 && !!user.id_card && user.isVerify === 1;
  const requiredEkyc = user.kyc === 1 && user.isVerify !== 1;
  const notSigned = isEkycCompleted && !user.isSigned;
  const isRequestVerifyPhone = !!user.isRequestVerifyPhone;
  const isVerifyPhone = !!user.isVerifyPhone;

  const dirty = cleanPhone(phone) !== (user.phone || "");

  const savePhone = (e: FormEvent) => {
    e.preventDefault();

    const value = cleanPhone(phone);

    if (!PHONE_RE.test(value)) {
      setPhoneErr("Số điện thoại chưa đúng, ví dụ 0901234567");

      return;
    }

    setPhoneErr("");
    run(
      "phone",
      () => AccountService.updateProfile({ phone: value }),
      (res) => {
        if (!res) return;

        setUser(res);
        setPhone(res.phone || "");
        dispatch(storeMyInfo(res));
        addToast({ text: t("Đã lưu số điện thoại liên hệ") });
      }
    );
  };

  // API đã che sẵn một phần thông tin (vd "*******2001"), hiện đúng như API trả về
  const kyc: [string, string | undefined, boolean?][] = [
    ["Họ và tên", user.fullName],
    ["Số CCCD/CMND", user.id_card],
    ["Ngày sinh", user.dob],
    ["Giới tính", user.gender],
    ["Ngày cấp", user.issueDate],
    ["Nơi cấp", user.issuePlace],
    ["Có giá trị đến", user.validDate],
    ["Loại giấy tờ", user.card_type],
    ["Địa chỉ thường trú", user.address, true],
  ];

  const helpLink = (children: ReactNode) => (
    <a
      href="#"
      className="font-semibold text-brand"
      onClick={(e) => {
        e.preventDefault();
        openSupport();
      }}
    >
      {children}
    </a>
  );

  return (
    <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[900px]:px-4 max-[760px]:gap-[18px]">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">
          {t("Tài khoản và xác minh danh tính")}
        </h1>
        <p className="m-0 mt-1 max-w-[62ch] text-muted">
          {t("Thông tin đăng nhập, trạng thái xác minh danh tính (eKYC) và bảo mật tài khoản.")}
        </p>
      </div>

      {(requiredEkyc || notSigned || (isRequestVerifyPhone && !isVerifyPhone)) && (
        <div className="flex flex-col gap-2.5">
          {requiredEkyc && (
            <Notice
              title="Bạn chưa thực hiện xác thực eKYC"
              text="Xác thực ngay để bảo mật tài khoản và mở khóa đầy đủ các tính năng trên hệ thống."
              to={PathnameUrl.EKYC_CONSENT}
              action="Xác thực ngay"
            />
          )}
          {notSigned && (
            <Notice
              title="Bạn chưa ký hợp đồng thuê máy"
              text="Chỉ còn 1 bước nữa. Ký ngay hợp đồng thuê máy để hoàn tất."
              to={PathnameUrl.IDENTITY_VERIFICATION}
              action="Ký ngay"
            />
          )}
          {isRequestVerifyPhone && !isVerifyPhone && (
            <Notice
              title="Bạn chưa xác minh số điện thoại"
              text="Xác minh ngay để bảo mật tài khoản và mở khóa đầy đủ các tính năng trên hệ thống."
              to={PathnameUrl.VERIFY_PHONE_CONSENT}
              action="Xác minh ngay"
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] items-start gap-4 max-[1100px]:grid-cols-1">
        {/* ---------- Thông tin định danh eKYC ---------- */}
        <section className={panel} aria-labelledby="ac-kyc-t">
          <div className={panelHead}>
            <div>
              <h2 id="ac-kyc-t" className={panelTitle}>
                <IcIdCard />
                {t("Thông tin định danh eKYC")}
              </h2>
              <p className="m-0 mt-0.5 text-[13px] text-muted">
                {t("Lấy từ giấy tờ bạn đã xác thực. Một phần thông tin được che để bảo mật.")}
              </p>
            </div>
            {isEkycCompleted ? (
              <span className={okPill}>
                <IcCheck className="h-[13px] w-[13px]" />
                {t("Đã xác thực")}
              </span>
            ) : (
              <span className={warnPill}>
                <IcAlert className="h-[13px] w-[13px]" />
                {t("Chưa xác thực")}
              </span>
            )}
          </div>
          <dl className="m-0 grid grid-cols-2 gap-2.5 px-5 py-4 max-[520px]:px-4 max-[359px]:grid-cols-1">
            {kyc.map(([k, v, wide]) => (
              <div key={k} className={clsx("min-w-0 rounded-[10px] bg-surface-2 px-3 py-2.5", wide && "col-span-full")}>
                <dt className="text-[11px] font-semibold uppercase tracking-[.05em] text-faint">{t(k)}</dt>
                <dd className="m-0 mt-0.5 font-semibold tabular-nums [overflow-wrap:anywhere]">{v || "---"}</dd>
              </div>
            ))}
          </dl>
          <p className="m-0 flex items-start gap-2 px-5 pb-[18px] text-[13px] text-muted max-[520px]:px-4 max-[520px]:pb-4">
            <IcInfo className="mt-0.5 h-4 w-4 flex-none" />
            <span>
              {t("Thông tin bị sai hoặc đã đổi giấy tờ?")} {helpLink(t("Liên hệ hỗ trợ"))} {t("để xác thực lại.")}
            </span>
          </p>
        </section>

        <div className="flex min-w-0 flex-col gap-4">
          {/* ---------- Thông tin đăng nhập ---------- */}
          <section className={panel} aria-labelledby="ac-log-t">
            <div className={panelHead}>
              <h2 id="ac-log-t" className={panelTitle}>
                <IcUser />
                {t("Thông tin đăng nhập")}
              </h2>
            </div>
            <div className="px-5 pb-3.5 pt-1.5 max-[520px]:px-4 max-[520px]:pb-3 max-[520px]:pt-1">
              <div className={row}>
                <span className="text-[13px] text-muted">{t("Tên đăng nhập")}</span>
                <span className="flex min-w-0 items-center gap-1.5">
                  <b className="text-right font-mono text-[12.5px] [overflow-wrap:anywhere]">{user.username}</b>
                  <CopyButton value={user.username} label="Sao chép tên đăng nhập" />
                </span>
              </div>
              <div className={row}>
                <span className="text-[13px] text-muted">{t("Email")}</span>
                <b className="text-right font-mono text-[12.5px] [overflow-wrap:anywhere]">{user.email}</b>
              </div>
              <form className="flex flex-col gap-1.5 pb-0.5 pt-3" noValidate onSubmit={savePhone}>
                <label htmlFor="ac-phone" className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-muted">
                  <span>
                    {t("Số điện thoại liên hệ")} <i className="not-italic text-bad">*</i>
                  </span>
                  {isVerifyPhone && !dirty && (
                    <span className={clsx(okPill, "!px-2 !py-px !text-[11px]")}>
                      <IcCheck className="h-[13px] w-[13px]" />
                      {t("Đã xác minh")}
                    </span>
                  )}
                  {isRequestVerifyPhone && !isVerifyPhone && (
                    <span className={clsx(warnPill, "!px-2 !py-px !text-[11px]")}>{t("Chưa xác minh")}</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    id="ac-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={MAX_LEN.PHONE}
                    className={clsx(input, "!h-10 min-w-0 flex-1 font-semibold tabular-nums", phoneErr && "!border-bad")}
                    value={phone}
                    readOnly={isVerifyPhone}
                    aria-invalid={!!phoneErr}
                    aria-describedby={phoneErr ? "ac-perr" : undefined}
                    onChange={(e) => {
                      setPhone(e.target.value);

                      if (phoneErr) setPhoneErr("");
                    }}
                  />
                  {!isVerifyPhone && (
                    <button type="submit" className={clsx(btn, btnPrimary, "flex-none")} disabled={!dirty || busy.phone}>
                      {busy.phone && <Spinner />}
                      {t("Lưu")}
                    </button>
                  )}
                </div>
                {phoneErr ? (
                  <small id="ac-perr" className="text-[11.5px] font-medium text-bad">
                    {t(phoneErr)}
                  </small>
                ) : (
                  <small className="text-[11.5px] text-faint">
                    {isVerifyPhone
                      ? t("Số điện thoại đã xác minh nên không đổi được.")
                      : t("PC Render gọi số này khi cần xác nhận đơn hàng.")}{" "}
                    {t("Cập nhật lần cuối")} <span className="tabular-nums">{formatDate(user.updatedAt)}</span>
                  </small>
                )}
              </form>
            </div>
          </section>

          {/* ---------- Bảo mật ---------- */}
          <section className={panel} aria-labelledby="ac-sec-t">
            <div className={panelHead}>
              <h2 id="ac-sec-t" className={panelTitle}>
                <IcKeyRound />
                {t("Bảo mật")}
              </h2>
            </div>
            <div className="px-5 pb-3.5 pt-1.5 max-[520px]:px-4 max-[520px]:pb-3 max-[520px]:pt-1">
              <div className={row}>
                <span className="flex min-w-0 flex-col text-[13px] text-muted">
                  {t("Mật khẩu")}
                  <small className="text-xs text-faint">{t("Đổi bằng liên kết gửi tới email")}</small>
                </span>
                <button
                  type="button"
                  className={clsx(btn, btnGhost, btnSm)}
                  onClick={() => openPortalDialog(ChangePasswordModal, { username: user.username, email: user.email })}
                >
                  <IcKeyRound className="h-4 w-4" />
                  {t("Đổi mật khẩu")}
                </button>
              </div>
              <div className={row}>
                <span className="flex min-w-0 flex-col text-[13px] text-muted">
                  {t("Đăng xuất")}
                  <small className="text-xs text-faint">{t("Thoát tài khoản trên thiết bị này")}</small>
                </span>
                <button type="button" className={clsx(btn, btnGhost, btnSm, "!text-bad")} onClick={logout}>
                  <IcLogOut className="h-4 w-4" />
                  {t("Đăng xuất")}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
